import { GET_LICENCES, useLazyGetLicences } from "../operations/queries/getLicences";
import LicencesList from "../components/LicencesList";
import { useAddLicence } from "../operations/mutations/addLicence";
import { useRenewLicence } from "../operations/mutations/renewLicence";
import { useCallback, useEffect, useState } from "react";
import { useArchiveLicence } from "../operations/mutations/archiveLicence";
import { useCancelLicenceAutoRenewal } from "../operations/mutations/cancelLicenceAutoRenewal";
import { useAppContext } from "../hooks/useAppContext";

const LicencesListContainer = () => {
  const [paginationInfo, setPaginationInfo] = useState({ pageSize: 0, pageIndex: 0 });
  const [orgFilter, setOrgFilter] = useState<string>();
  const [isArchivedFilter, setIsArchivedFilter] = useState<boolean | null>(false);
  const { currentOrganization, organizationConfiguration } = useAppContext();
  const { getLicences, data: licences, status: getLicencesStatus } = useLazyGetLicences();
  const { mutate: addLicenceMutate, status: addLicenceStatus } = useAddLicence();
  const { mutate: renewLicenceMutate, status: renewLicenceStatus } = useRenewLicence();
  const { mutate: cancelLicenceAutoRenewalMutate, status: cancelLicenceAutoRenewalStatus } =
    useCancelLicenceAutoRenewal();
  const { mutate: archiveLicenceMutate, status: archiveLicenceStatus } = useArchiveLicence();

  const handlePageInfoChange = useCallback((pageIndex: number, pageSize: number) => {
    if (pageSize !== paginationInfo.pageSize || pageIndex !== paginationInfo.pageIndex) {
      setPaginationInfo({ pageSize, pageIndex });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOrgFilter = (orgId: string) => {
    if (orgId !== undefined) setOrgFilter(orgId);
    setPaginationInfo({ ...paginationInfo, pageIndex: 0 });
  };

  const handleIsArchivedFilter = (isArchived: boolean | null) => {
    setIsArchivedFilter(isArchived);
  };

  useEffect(() => {
    if (currentOrganization && paginationInfo.pageSize > 0) {
      getLicences({
        variables: {
          skip: paginationInfo.pageIndex * paginationInfo.pageSize,
          take: paginationInfo.pageSize,
          orgId: currentOrganization.id,
          isArchived: isArchivedFilter,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationInfo.pageSize, paginationInfo.pageIndex, orgFilter, isArchivedFilter, currentOrganization]);

  const handleAddLicence = (productName: string, totalPrice: number, numberOfUsers: number, licenceType: string) => {
    if (currentOrganization) {
      addLicenceMutate({
        refetchQueries: [
          {
            query: GET_LICENCES,
            variables: {
              skip: paginationInfo.pageIndex * paginationInfo.pageSize,
              take: paginationInfo.pageSize,
              orgId: currentOrganization.id,
              isArchived: isArchivedFilter,
            },
          },
        ],
        variables: {
          productName,
          totalPrice,
          numberOfUsers,
          licenceType,
          organizationId: currentOrganization.id,
        },
      });
    }
  };

  const handleRenewLicence = (
    productName: string,
    totalPrice: number,
    numberOfUsers: number,
    licenceType: string,
    licenceId: string
  ) => {
    if (currentOrganization) {
      renewLicenceMutate({
        refetchQueries: [
          {
            query: GET_LICENCES,
            variables: {
              skip: paginationInfo.pageIndex * paginationInfo.pageSize,
              take: paginationInfo.pageSize,
              orgId: currentOrganization.id,
              isArchived: isArchivedFilter,
            },
          },
        ],
        variables: {
          productName,
          totalPrice,
          numberOfUsers,
          licenceType,
          licenceId,
          organizationId: currentOrganization.id,
        },
      });
    }
  };

  const handelCancelAutoRenewal = (licenceId: string) => {
    if (!currentOrganization) return;

    cancelLicenceAutoRenewalMutate({
      variables: {
        organizationId: currentOrganization.id,
        licenceId,
      },
    });
  };

  const handleToggleArchivedStatusLicence = (id: string) => {
    if (!currentOrganization) return;

    archiveLicenceMutate({
      variables: {
        organizationId: currentOrganization.id,
        id,
      },
    });
  };

  return (
    <LicencesList
      isLicencesLoading={getLicencesStatus.loading}
      licences={licences && licences.licences?.items}
      pageCount={licences && Math.ceil((licences.licences?.totalCount ?? 0) / paginationInfo.pageSize)}
      onPageInfoChange={handlePageInfoChange}
      onOrgFilter={handleOrgFilter}
      onAddLicence={handleAddLicence}
      addLicenceStatus={addLicenceStatus}
      onRenewLicence={handleRenewLicence}
      renewLicenceStatus={renewLicenceStatus}
      onArchiveLicence={handleToggleArchivedStatusLicence}
      archiveLicenceStatus={archiveLicenceStatus}
      onCancelAutoRenewal={handelCancelAutoRenewal}
      cancelAutoRenewalStatus={cancelLicenceAutoRenewalStatus}
      isArchivedFilterValue={isArchivedFilter}
      onIsArchivedFilter={handleIsArchivedFilter}
      products={organizationConfiguration?.licencing?.products}
    ></LicencesList>
  );
};

export default LicencesListContainer;
